<template>
  <emat-popup :menu="menu" @close="$emit('close')" :state="popupState">
    <template #header>
      <div class="title">
        <p v-if="state === states.PENDING" style="margin: 0;">{{ $t('OrderPopup.adding_to_cart') }}</p>
        <div class="title" v-else-if="state === states.SUCCESS" style="margin: 0;">
          <GreenCheckSvg v-if="!hasNotFoundProducts" />
          <WarningCircle v-else />
          <span style="margin-left: 5px;">{{ $t('OrderPopup.added_to_cart') }}</span>
        </div>
        <p v-else-if="state === states.FAILED" style="margin: 0;">{{ $t('OrderPopup.something_went_wrong') }}</p>
      </div>
    </template>
    <template #body>
      <emat-loader v-if="state === states.PENDING" :loading="true" :container="true" :order="true" :gif-url="loaderUrl" />

      <template v-else-if="state === states.SUCCESS">
        <order-popup-sub-header />
        <order-popup-body-success :price="price" :btw="btw" />
        <div class="delivery-time">
          <GreenCheckSvg />
          <span class="time">Levertijd <span class="strong">{{ deliveryTime }}</span></span>
        </div>
      </template>
    </template>
    <template #footer>
      <order-popup-footer-success v-if="state === states.SUCCESS" @close="$emit('close')" :cart-url="cartUrl" :shop-url="shopUrl" />
      <order-popup-body-danger v-else-if="state === states.FAILED" :try-again="build" />
    </template>
  </emat-popup>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { makeBuild } from '@/services/api';
import EmatPopup from 'Components/Layout/Popup/index';
import EmatLoader from 'Components/General/Loader/index';
import OrderPopupSubHeader from '@/components/order/OrderPopupSubHeader';
import OrderPopupBodySuccess from '@/components/order/OrderPopupBodySuccess';
import OrderPopupFooterSuccess from '@/components/order/OrderPopupFooterSuccess';
import OrderPopupBodyDanger from '@/components/order/OrderPopupBodyDanger';
import GreenCheckSvg from '../../assets/images/icons/checkmark-green.svg?inline';
import LoaderGif from '../../assets/images/loader-laadpaal.gif';
import { sendToMagentoCart } from '@/services/magentoCart';
import WarningCircle from '../../assets/images/icons/warning-circle.svg?inline';

export default {
  name: 'OrderPopup',

  props: {
    menu: {
      required: true,
      type: Boolean,
    },
    price: String,
    btw: String,
  },

  components: {
    OrderPopupBodyDanger,
    OrderPopupFooterSuccess,
    EmatPopup,
    EmatLoader,
    OrderPopupSubHeader,
    OrderPopupBodySuccess,
    GreenCheckSvg,
    LoaderGif,
    WarningCircle,
  },

  data() {
    return {
      state: null,
      addCartUrl: null,
      cartUrl: null,
      shopUrl: null,
      loaderGif: LoaderGif,
    };
  },

  computed: {
    ...mapState([
      'environmentId',
    ]),
    ...mapGetters('build', [
      'getBuild',
      'deliveryDays',
      'getProductsAndWishes'
    ]),
    ...mapGetters('order', {
      orderAmount: 'getOrderAmount',
      notFoundProductSkus: 'getNotFoundProductSkus',
    }),

    loaderUrl() {
      return process.env.VUE_APP_URL + this.loaderGif;
    },

    hasNotFoundProducts() {
      return !!this.notFoundProductSkus.length;
    },

    states() {
      return {
        PENDING: 'pending',
        SUCCESS: 'success',
        FAILED: 'failed',
      };
    },

    popupState() {
      switch (this.state) {
        case this.states.SUCCESS:
          return 'success';
        case this.states.FAILED:
          return 'error';
        default:
          return null;
      }
    },

    deliveryTime() {
      if (this.deliveryDays === 1) {
        return this.$t('DeliveryTime.delivered_tomorrow', { days: this.deliveryDays });
      } else {
        return this.$t('DeliveryTime.delivered_in_days', { days: this.deliveryDays });
      }
    },
  },

  methods: {
    build() {
      this.state = this.states.PENDING;
      makeBuild(this.environmentId, this.getBuild)
        .then(async ({ data }) => {
          await this.addToMagentoCart(data.sku)
          this.cartUrl = data.cart_url;
          this.shopUrl = data.shop_url;

          this.success();
        }).catch(response => {
        console.log(response);
        this.state = this.states.FAILED;
      });
    },

    async addToMagentoCart(buildSku) {
      const options = [];
      if (this.getProductsAndWishes.wishes) {
        const extraWishesItem = {
          title: 'Extra wensen',
          content: this.getProductsAndWishes.wishes,
        };
        options.push(extraWishesItem);
      }

      const products = [
        {
          sku: buildSku,
          qty: this.orderAmount,
          options: options,
        },
      ];

      this.getProductsAndWishes.products.all.forEach((product) => {
        let productItem = {
          sku: product.sku,
          qty: product.amount,
          options: [],
        };
        products.push(productItem);
      });

      await sendToMagentoCart(products).then((response) => {
        switch (response.status) {
          case 200:
            break;
          case 202:
            let notFoundProductSkus = response.data.map((product) => {
              return product['productSku'];
            });
            this.$store.commit(
                'order/SET_NOT_FOUND_PRODUCT_SKUS',
                notFoundProductSkus
            );
            break;
          default:
            throw 'Unknown situation';
        }
      });
    },

    success() {
      setTimeout(() => {
        this.state = this.states.SUCCESS;
      }, 500);
    },
  },

  created() {
    this.state = this.states.PENDING;
    this.build();
  },
};
</script>

<style lang="scss" scoped>
.delivery-time {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: $highlight;
  height: 32px;

  .time {
    margin-left: 4px;
    font-size: 12px;
    font-family: $font;

    .strong {
      font-family: $font-black;
    }
  }
}

.title {
  display: flex;
  align-items: center;
  color: $primary;
}
</style>
